import dayjs from 'dayjs'
import Debug from 'debug'
import { jwtDecode } from 'jwt-decode'
import { ReactNode, createContext, useEffect, useMemo } from 'react'
import { UseQueryExecute, useQuery } from 'urql'
import { useLocalStorage } from 'usehooks-ts'
import { graphql } from '../../gql'
import { AuthProviderUserQuery } from '../../gql/operations'
import { AcceptInvitation } from './AcceptInvitation'
import { CreateOrg } from './CreateOrg'
import { SignIn } from './SignIn'

const log = Debug('ela:AuthProvider')
log.log = console.info.bind(console)

export type TAuthContext = {
  token?: string
  user?: AuthProviderUserQuery['User']
  activeOrganization?: string
  setActiveOrganization?: (orgId: string) => void
  refreshUser: UseQueryExecute
}

export type TokenContent = {
  email: string
  sub: string
  exp: number
  iat: number
}

export const AuthContext = createContext<TAuthContext>({} as TAuthContext)

export type AuthProviderProps = {
  children: ReactNode
}

const userQuery = graphql(`
  query AuthProviderUser {
    User {
      id
      email
      givenName
      familyName
      image
      orgs {
        id
        name
        role
      }
    }
  }
`)

export default function AuthProvider(props: AuthProviderProps) {
  const { children } = props
  const [token, setToken] = useLocalStorage<string | undefined>('token', undefined, {
    deserializer: (value) => value,
    serializer: (value) => value || '',
  })

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode<TokenContent>(token)
      const willExpireAt = new Date(decodedToken.exp * 1000)
      const expiresIn_ms = willExpireAt.getTime() - Date.now()
      const d = dayjs(willExpireAt)
      log(`Token will expire ${d.fromNow()}`)
      const timeoutId = setTimeout(() => {
        console.warn('Token expired. Bye bye! 👋')
        setToken(undefined)
      }, expiresIn_ms)
      return () => clearTimeout(timeoutId)
    }
  }, [token, setToken])

  const [result, refreshUser] = useQuery({
    query: userQuery,
    pause: !token,
  })
  const { data, fetching, error } = result

  const [activeOrganization, setActiveOrganization] = useLocalStorage<string | undefined>(
    'activeOrganization',
    undefined,
    {
      deserializer: (value) => value,
      serializer: (value) => value || '',
    },
  )

  const [postloginPath, setPostloginPath] = useLocalStorage<string | undefined>('postloginPath', undefined, {
    deserializer: (value) => value,
    serializer: (value) => value || '',
  })

  const invitationId = useMemo(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (url.pathname.includes('accept-invitation') === false) return
    let token = url.pathname.split('accept-invitation/').pop()
    if (!token) return
    token = token.replace(/^\//, '').replace(/\/$/, '').trim()
    if (token === '') return
    console.log('Invitation token:', token)
    return token
  }, [])

  useEffect(() => {
    const orgs = data?.User?.orgs
    if (!orgs) return
    if (!activeOrganization || !orgs.map((o) => o.id).includes(activeOrganization)) {
      if (orgs.length > 0) {
        setActiveOrganization(data.User.orgs[0].id)
      }
    }
  }, [activeOrganization, data?.User.orgs, setActiveOrganization])

  const handleOrgCreated = (orgId: string) => {
    log(`Org created: "${orgId}". Refreshing user...`)
    refreshUser({ requestPolicy: 'network-only' })
  }

  const handleInvitationAccepted = () => {
    log('Invitation accepted. Refreshing user...')
    refreshUser({ requestPolicy: 'network-only' })
  }

  useEffect(() => {
    if (!token) {
      log('No token. Redirecting to sign in...')
      log('Setting postlogin path:', window.location.pathname)
      setPostloginPath(window.location.pathname)
    }
  }, [token, setPostloginPath])

  if (fetching) return <div>Loading...</div>
  if (error) {
    console.warn(error)
    return <SignIn />
  }

  if (!token) {
    return <SignIn />
  }

  if (invitationId) {
    return <AcceptInvitation invitationId={invitationId} onCompleted={handleInvitationAccepted} />
  }

  if (!activeOrganization || (data?.User?.orgs && data.User.orgs.length === 0)) {
    return <CreateOrg userGivenName={data?.User.givenName} onCreated={handleOrgCreated} />
  }

  return (
    <AuthContext.Provider
      value={{
        token,
        user: data?.User,
        activeOrganization,
        setActiveOrganization,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
